import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import type { CapabilityId, TopBarSurfaceId, GlobalPanelSurfaceId, EntryPointSurfaceId } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// WORKSPACE ALLOWLIST REGISTRY
// ─────────────────────────────────────────────────────────────────────────────
//
// Deny-by-default: if a capability is NOT in ALLOWED_FEATURES, it is BLOCKED
// in workspace scope. All surfaces (top-bar, panels, entry-points) inherit their
// decision from a parent CapabilityId via the maps below.
//
// Non-workspace scopes (location, org, division) are unrestricted — everything
// is available regardless of this registry.
//
// TO CHANGE WORKSPACE ACCESS: edit ALLOWED_FEATURES below. That's it.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The single source of truth for workspace scope access.
 *
 * Any CapabilityId (feature or independent panel) listed here is ALLOWED.
 * Everything else is BLOCKED. Surfaces inherit via parent maps.
 *
 * Examples of what being allowed means:
 * - 'messages' → /messages route accessible, /settings/messaging accessible,
 *   dashboard.messages tile shown, quick-action.new-message enabled,
 *   call-insights panel accessible (because its parent is 'messages')
 * - 'task-center' → task-center-button in top-bar shown,
 *   dashboard.task-center tile shown
 */
export const ALLOWED_FEATURES: ReadonlySet<CapabilityId> = new Set<CapabilityId>([
  // Features (route-based)
  'messages',
  'analytics',
  // Panels (slide-in, independent of routes)
  'task-center',
]);

/**
 * Standalone settings sections allowed in workspace scope.
 *
 * These are /settings/<section> paths that don't belong to any feature or
 * standalone capability. They have their own independent allow/block decision.
 *
 * Settings sections tied to a feature (e.g. /settings/phone → 'calls') inherit
 * from the feature via path resolution — they don't need an entry here.
 */
export const ALLOWED_SETTINGS: ReadonlySet<string> = new Set(['organization', 'personal']);

// ─────────────────────────────────────────────────────────────────────────────
// Surface → Capability parent maps
// ─────────────────────────────────────────────────────────────────────────────
//
// Every surface resolves to a parent CapabilityId. The decision resolver checks
// the parent against the scope's allowlist. This means:
// - Adding a surface only requires adding an entry to its parent map
// - The surface inherits its parent's allow/block decision automatically
// - No separate "surface allowlist" to maintain
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Maps each top-bar surface to its parent capability.
 *
 * Multiple surfaces can share a parent — e.g. 'quickfill-button' and
 * 'quickfill-panel' both inherit from 'schedule'. If schedule is blocked,
 * both surfaces are blocked.
 *
 * Top-bar buttons that toggle a global panel point to that panel's capability.
 */
export const TOP_BAR_PARENTS: Record<TopBarSurfaceId, CapabilityId> = {
  'team-chat-button': 'team-chat',
  'dialpad-button': 'calls',
  'quickfill-button': 'schedule',
  'quickfill-panel': 'schedule',
  'schedule-pulse-button': 'schedule',
  'schedule-pulse-panel': 'schedule',
  'task-center-button': 'task-center',
};

/**
 * Maps each global panel surface to its parent capability.
 *
 * Panels that are their own independent capability (team-chat, task-center)
 * map to themselves.
 * Panels like call-insights, voicemail-details map to messages parent
 * as they can be accessed from message feature in a workspace. (from a Inbox thread).
 * Panels message-insights is mapped to its parent feature.
 */
export const PANEL_PARENTS: Record<GlobalPanelSurfaceId, CapabilityId> = {
  'team-chat': 'team-chat',
  'task-center': 'task-center',
  'call-insights': 'messages',
  'voicemail-details': 'messages',
  'message-insights': 'messages',
};

/**
 * Maps each entry-point surface to its parent capability.
 *
 * Entry points (dashboard tiles, quick actions, shortlinks) inherit their
 * access from their parent. No separate entry-point allowlist needed —
 * if the parent capability is allowed, the entry point is allowed.
 *
 * Design decisions:
 * - 'dashboard.voicemails' → 'calls' (voicemails are a filtered view of calls)
 * - 'dashboard.lists' → 'contacts' (contact lists live under contacts)
 * - 'dashboard.team-chat' → 'team-chat' (standalone capability)
 * - 'dashboard.task-center' → 'task-center' (standalone capability)
 * - 'shortlink.*' → parent feature of the panel they open
 */

export const ENTRY_POINT_PARENTS: Record<EntryPointSurfaceId, CapabilityId> = {
  'dashboard.home': 'home',
  'dashboard.calls': 'calls',
  'dashboard.voicemails': 'calls',
  'dashboard.messages': 'messages',
  'dashboard.contacts': 'contacts',
  'dashboard.lists': 'contacts',
  'dashboard.schedule.route': 'schedule',
  'dashboard.schedule.pulse': 'schedule',
  'dashboard.forms': 'forms',
  'dashboard.reviews': 'reviews',
  'dashboard.analytics': 'analytics',
  'dashboard.payments': 'payments',
  'dashboard.fax': 'fax',
  'dashboard.team-chat': 'team-chat',
  'dashboard.task-center': 'task-center',
  'dashboard.quickfill': 'schedule',
  'quick-action.new-message': 'messages',
  'quick-action.request-payment': 'payments',
  'quick-action.new-appointment': 'schedule',
  'quick-action.new-out-of-office': 'schedule',
  'quick-action.new-fax': 'fax',
  'quick-action.new-review-request': 'reviews',
  'shortlink.voicemail-details': 'calls',
  'shortlink.call-insights': 'calls',
};

/**
 * Popup notification types allowed in workspace scope.
 *
 * Same pattern as ALLOWED_FEATURES — only list what should show.
 * Everything else is denied. No exhaustive registry to maintain.
 */
export const ALLOWED_NOTIFICATIONS: ReadonlySet<string> = new Set([
  // Messaging
  'sms-message-new',
  'sms-tag',
  'voicemail-message',
  'voicemail-tag',
  'inbox-robocall-tag',
  'call-record-tag',
  // AIR
  'ai-receptionist-state',
  // Task Center
  'task-center',
  'task-center-ai-settings',
]);

/**
 * Notification Center (Firestore persisted) types allowed in workspace scope.
 *
 * Same deny-by-default pattern as ALLOWED_NOTIFICATIONS above, but for the
 * persisted list in the Notification Center tray. Uses Firestore
 * `NotificationType` enum values instead of popup type strings.
 *
 * Types not listed here are hidden from the Notification Center when in
 * workspace scope. Non-workspace scopes are unrestricted.
 */
export const ALLOWED_IN_NOTIFICATION_CENTER: ReadonlySet<string> = new Set([
  // ── Messages ──────────────────────────────────────────────────────────────
  NotificationType.SMS,
  NotificationType.SMS_TAG_V2,
  NotificationType.INBOX_THREAD_ITEM,
  NotificationType.INBOX_THREAD_ITEM_TAG,
  NotificationType.ROBOCALL_TAG,
  NotificationType.SMS_TASK,
  NotificationType.SMS_MANUAL_SCHEDULED,
  NotificationType.SMS_PAUSE_SCHEDULED_THREAD,
  // ── Calls / Voicemail ─────────────────────────────────────────────────────
  NotificationType.VOICEMAIL_MESSAGE,
  NotificationType.CALL_RECORD_TAG,
  NotificationType.VOICEMAIL_TAG,
  // ── Task Center ───────────────────────────────────────────────────────────
  NotificationType.TASK,
  NotificationType.TASK_CENTER_AI_AUTO_ASSIGN,
  // ── AI Receptionist ───────────────────────────────────────────────────────
  NotificationType.AI_RECEPTIONIST_STATE,
]);
